import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X as XIcon, Send } from 'lucide-react';
import { Button } from '../../ui';
import SearchableSelect from './SearchableSelect';
import HelpTip from './HelpTip';
import { api } from '../api';
import {
  LOS_DOS, ETIQUETA_AMBITO, agruparPorSlug, opcionesAmbito, etiquetaTipo, ambitoInicial,
  ambitoValido, scopeDelPayload, tiposDelAmbito, camposDelAmbito,
} from '../utils/ambitoRegla';
import { fetchUsuariosAsignables } from '../../../../peticiones_API/PeticionesPermisos';
import { fetchColaboradoresParaSelector } from '../../../../peticiones_API/PeticionesPersonasColaboradoras';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * NotifRuleForm (Fase A)
 *
 * Form de alta/edición de una NotificationRule. Soporta dos modos:
 *
 *   trigger_kind = 'evento' (por defecto)
 *     - trigger_event obligatorio (ENUM del modelo)
 *     - asset_type_slug opcional (vacío = TODOS los tipos)
 *
 *   trigger_kind = 'fecha'
 *     - asset_type_slug OBLIGATORIO (no "Todos los tipos")
 *     - date_field: nombre de un campo `type:'date'` del fields_schema del tipo
 *     - days_before: entero >= 0
 *
 * Resto de campos comunes:
 *   - nombre (obligatorio, <=100)
 *   - recipients (chips de email; al menos uno)
 *   - subject (<=255)  y  body (HTML, react-quill)
 *   - is_active (default true)
 *
 * El motor de envío (Fases B/C) resolverá las variables `{{activo}} {{tipo}}
 * {{fecha}}` (comunes) y `{{evento}} {{usuario}}` o `{{campo}} {{dias}}` según
 * el modo. Aquí solo se guardan tal cual.
 *
 * Botón "Enviar email de prueba": placeholder; se activa en Fase A.2 cuando
 * exista la plantilla contenedor de SendGrid.
 *
 * Props: initial, onSubmit, onCancel (mismo contrato que StockAlertForm).
 */

const TRIGGER_EVENTS = [
  { id: 'asignacion', nombre: 'Asignación' },
  { id: 'devolucion', nombre: 'Devolución' },
  { id: 'baja', nombre: 'Baja' },
  { id: 'reparacion_inicio', nombre: 'Entrada a reparación' },
  { id: 'reparacion_fin', nombre: 'Fin de reparación' },
  { id: 'stock_bajo', nombre: 'Stock bajo' },
];

const VARIABLES_COMUNES = [
  { id: '{{activo}}', label: 'Activo (referencia)' },
  { id: '{{tipo}}', label: 'Tipo de artículo' },
  { id: '{{fecha}}', label: 'Fecha del evento / vencimiento' },
];
const VARIABLES_EVENTO = [
  { id: '{{evento}}', label: 'Nombre del evento' },
  { id: '{{usuario}}', label: 'Usuario implicado' },
];
const VARIABLES_FECHA = [
  { id: '{{campo}}', label: 'Campo fecha (date_field)' },
  { id: '{{dias}}', label: 'Días de antelación' },
];
// Para reglas de evento "stock_bajo": el aviso no va por activo individual sino
// por agregado (tipo, empresa, cantidad disponible, mínimo configurado).
const VARIABLES_STOCK = [
  { id: '{{tipo}}', label: 'Tipo de artículo' },
  { id: '{{empresa}}', label: 'Empresa con stock bajo' },
  { id: '{{cantidad}}', label: 'Unidades disponibles' },
  { id: '{{minimo}}', label: 'Stock mínimo configurado' },
  { id: '{{fecha}}', label: 'Fecha del aviso' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmail = (s) => EMAIL_RE.test(String(s).trim());

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet'];

function parseRecipientsToChips(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr.map((s) => String(s).trim()).filter(Boolean);
  } catch (_) { /* no JSON, sigo */ }
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
}

export default function NotifRuleForm({ initial, onSubmit, onCancel }) {
  const isEdit = !!initial;

  const [nombre, setNombre] = useState(initial?.nombre || '');
  const [triggerKind, setTriggerKind] = useState(initial?.trigger_kind || 'evento');
  const [triggerEvent, setTriggerEvent] = useState(initial?.trigger_event || '');
  // Tipo y ámbito, por separado (Fase 4, utils/ambitoRegla.js): el tipo se elige por SLUG, una vez
  // («Coche»), y el ámbito aparte — 'interno' | 'colab' | 'los_dos' (= scope NULL en la base), con sólo
  // las opciones que existen para ese slug. Los campos (fecha, usuario) salen del tipo del ámbito
  // elegido; con «Los dos», los comunes a los dos gemelos.
  const [slugSel, setSlugSel] = useState(initial?.asset_type_slug || '');
  const [ambito, setAmbito] = useState('');
  const [dateField, setDateField] = useState(initial?.date_field || '');
  const [daysBefore, setDaysBefore] = useState(
    initial?.days_before !== null && initial?.days_before !== undefined
      ? String(initial.days_before)
      : ''
  );
  const [chips, setChips] = useState(parseRecipientsToChips(initial?.recipients));
  const [chipInput, setChipInput] = useState('');
  const [chipError, setChipError] = useState(null);
  const [notifyAssignee, setNotifyAssignee] = useState(!!initial?.notify_assignee);
  const [notifyInapp, setNotifyInapp] = useState(!!initial?.notify_inapp);
  const [assigneeField, setAssigneeField] = useState(initial?.assignee_field || '');
  // Personas internas seleccionadas como destinatarios: guardan Id; el email lo
  // resuelve el motor al enviar (siempre actual). [{ id, nombre }]
  const [personasSel, setPersonasSel] = useState([]);
  // Colaboradores seleccionados (mismo patrón, contra personas_colaboradoras).
  const [colabSel, setColabSel] = useState([]);
  const [subject, setSubject] = useState(initial?.subject || '');
  const [body, setBody] = useState(initial?.body || '');
  const [isActive, setIsActive] = useState(
    initial?.is_active !== undefined ? !!initial.is_active : true
  );

  const [tipos, setTipos] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(true);
  const [personas, setPersonas] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testMsg, setTestMsg] = useState(null);

  const quillRef = useRef(null);
  const subjectRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setTiposLoading(true);
      try {
        const res = await api.get('/tipos');
        if (alive) setTipos(res?.data || []);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setTiposLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Personas internas para el selector de destinatarios (opción A: reutiliza
  // /api/permisos/usuarios-asignables). Si falla, el form sigue funcionando
  // con email tecleado a mano.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchUsuariosAsignables();
        if (alive) setPersonas(Array.isArray(data) ? data : []);
      } catch (_) { /* silencioso: el form sigue con email a mano */ }
    })();
    return () => { alive = false; };
  }, []);

  // Al editar: parsear initial.recipient_person_ids (CSV de Id) y mapear cada
  // Id a { id, nombre } usando el catálogo de personas. Se ejecuta cuando ya
  // están cargadas las personas (sin ellas no podemos resolver nombre).
  useEffect(() => {
    if (!initial?.recipient_person_ids || personas.length === 0) return;
    const ids = String(initial.recipient_person_ids)
      .split(',').map((s) => Number(s.trim())).filter(Boolean);
    setPersonasSel(ids.map((id) => {
      const p = personas.find((x) => x.Id === id);
      const nombre = p
        ? [p.nombre, p.apellidos].filter(Boolean).join(' ') || `#${id}`
        : `#${id}`;
      return { id, nombre };
    }));
  }, [initial, personas]);

  // Carga de colaboradores para el selector (paralelo al de personas).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchColaboradoresParaSelector();
        if (alive) setColaboradores(Array.isArray(data) ? data : []);
      } catch (_) { /* silencioso */ }
    })();
    return () => { alive = false; };
  }, []);

  // Rehidratar colabSel al editar.
  useEffect(() => {
    if (!initial?.recipient_colab_ids || colaboradores.length === 0) return;
    const ids = String(initial.recipient_colab_ids)
      .split(',').map((s) => Number(s.trim())).filter(Boolean);
    setColabSel(ids.map((id) => {
      const p = colaboradores.find((x) => x.Id === id);
      const nombre = p
        ? [p.nombre, p.apellidos].filter(Boolean).join(' ') || `#${id}`
        : `#${id}`;
      return { id, nombre };
    }));
  }, [initial, colaboradores]);

  // Un grupo por slug: { slug, nombre, porScope, scopes }.
  const grupos = useMemo(() => agruparPorSlug(tipos), [tipos]);

  const tipoOptions = useMemo(
    () => [
      { id: '', nombre: 'Todos los tipos' },
      ...grupos.map((g) => ({ id: g.slug, nombre: etiquetaTipo(g) })),
    ],
    [grupos]
  );

  // En modo fecha, "Todos los tipos" no es válido → ocultamos esa opción
  const tipoOptionsFecha = useMemo(
    () => tipoOptions.filter((o) => o.id !== ''),
    [tipoOptions]
  );

  // Grupo del slug elegido (null con «Todos los tipos» o si el catálogo aún no cargó).
  const grupoActual = useMemo(
    () => (slugSel ? grupos.find((g) => g.slug === slugSel) || null : null),
    [grupos, slugSel]
  );
  // El resto del form razona en términos de slug (es lo que persiste la regla).
  const slug = grupoActual ? grupoActual.slug : '';
  // Mientras no se elija ámbito (tipo en los dos), los campos que se ofrecen son los comunes.
  const ambitoCampos = ambito || LOS_DOS;
  // Tipo de referencia: el del ámbito elegido (para el nombre del ejemplo de la vista previa).
  const tipoActual = tiposDelAmbito(grupoActual, ambitoCampos)[0] || null;

  // Ámbito al ABRIR, cuando el catálogo ya cargó: al editar, el de la regla (NULL → «Los dos» si el
  // tipo está en los dos); al crear, el único que haya. Una sola vez: después lo cambia el usuario.
  const ambitoIniciado = useRef(false);
  useEffect(() => {
    if (ambitoIniciado.current || grupos.length === 0) return;
    ambitoIniciado.current = true;
    if (grupoActual) setAmbito(ambitoInicial(grupoActual, { editando: isEdit, scope: initial?.scope ?? null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos]);

  // Campos `type:'date'` / `type:'user_link'` del tipo del ámbito (con «Los dos», los comunes).
  const dateFieldOptions = useMemo(
    () => camposDelAmbito(grupoActual, ambitoCampos, (f) => f && f.type === 'date'),
    [grupoActual, ambitoCampos]
  );
  const userLinkOptions = useMemo(
    () => camposDelAmbito(grupoActual, ambitoCampos, (f) => f && f.type === 'user_link'),
    [grupoActual, ambitoCampos]
  );

  // Al elegir OTRO tipo: el ámbito vuelve al inicial de ese tipo (el único, o ninguno si hay dos).
  const elegirTipo = (v) => {
    setSlugSel(v || '');
    setAmbito(ambitoInicial(v ? grupos.find((g) => g.slug === v) : null));
  };
  // Al cambiar de ámbito, un campo elegido que el nuevo ámbito no tiene se vacía.
  const elegirAmbito = (v) => {
    setAmbito(v);
    const tiene = (filtro, valor) => !valor || camposDelAmbito(grupoActual, v || LOS_DOS, filtro).some((o) => o.id === valor);
    if (!tiene((f) => f && f.type === 'date', dateField)) setDateField('');
    if (!tiene((f) => f && f.type === 'user_link', assigneeField)) setAssigneeField('');
  };
  const selectorAmbito = grupoActual ? (
    <div>
      <label className="text-xs text-slate-500 mb-1 block" htmlFor="regla-ambito">Ámbito</label>
      <select
        id="regla-ambito"
        value={ambito}
        onChange={(e) => elegirAmbito(e.target.value)}
        disabled={grupoActual.scopes.length === 1}
        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500"
      >
        {!ambito && <option value="">Elige el ámbito</option>}
        {opcionesAmbito(grupoActual).map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
      </select>
      <p className="text-[11px] text-slate-400 mt-1">
        {grupoActual.scopes.length === 1
          ? `Este tipo sólo existe en ${ETIQUETA_AMBITO[grupoActual.scopes[0]]}.`
          : 'De qué activos avisa: GRUPOLAE, Colaboradores, o los dos.'}
      </p>
    </div>
  ) : null;

  // Personas internas con email, excluyendo las que ya están seleccionadas.
  // El `id` de la opción es el Id de la persona (no su email) → se guarda
  // como recipient_person_ids para que el motor resuelva el email al enviar.
  const personaOptions = useMemo(
    () => personas
      .filter((p) => p.mail_empresa && !personasSel.some((s) => s.id === p.Id))
      .map((p) => ({
        id: String(p.Id),
        nombre: `${[p.nombre, p.apellidos].filter(Boolean).join(' ')} · ${p.mail_empresa}`,
      })),
    [personas, personasSel]
  );

  const colabOptions = useMemo(
    () => colaboradores
      .filter((p) => p.mail_empresa && !colabSel.some((s) => s.id === p.Id))
      .map((p) => ({
        id: String(p.Id),
        nombre: `${[p.nombre, p.apellidos].filter(Boolean).join(' ')} · ${p.mail_empresa}`,
      })),
    [colaboradores, colabSel]
  );

  const variablesDisponibles = useMemo(() => {
    if (triggerKind === 'fecha') return [...VARIABLES_COMUNES, ...VARIABLES_FECHA];
    if (triggerEvent === 'stock_bajo') return [...VARIABLES_STOCK];
    return [...VARIABLES_COMUNES, ...VARIABLES_EVENTO];
  }, [triggerKind, triggerEvent]);

  // ── chips de email ─────────────────────────────────────────────
  function commitChip(rawValue) {
    const v = String(rawValue || '').trim().replace(/,$/, '').trim();
    if (!v) return;
    if (!isEmail(v)) { setChipError(`"${v}" no es un email válido`); return; }
    if (chips.includes(v)) { setChipInput(''); setChipError(null); return; }
    setChips([...chips, v]);
    setChipInput('');
    setChipError(null);
  }
  function handleChipKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitChip(chipInput);
    } else if (e.key === 'Backspace' && !chipInput && chips.length) {
      setChips(chips.slice(0, -1));
    }
  }
  function handleChipBlur() {
    if (chipInput.trim()) commitChip(chipInput);
  }
  function removeChip(idx) {
    setChips(chips.filter((_, i) => i !== idx));
  }

  // ── insertar variable ──────────────────────────────────────────
  function insertarEnSubject(token) {
    const el = subjectRef.current;
    if (!el) { setSubject((s) => s + token); return; }
    const start = el.selectionStart ?? subject.length;
    const end = el.selectionEnd ?? subject.length;
    const next = subject.slice(0, start) + token + subject.slice(end);
    setSubject(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      try { el.setSelectionRange(pos, pos); } catch (_) {}
    });
  }
  function insertarEnBody(token) {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) { setBody((b) => b + token); return; }
    const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
    quill.insertText(range.index, token, 'user');
    quill.setSelection(range.index + token.length, 0, 'user');
  }

  // ── submit ─────────────────────────────────────────────────────
  async function handleSubmit() {
    setErr(null);
    if (!nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (triggerKind === 'evento' && !triggerEvent) {
      setErr('Selecciona un evento que dispare la regla.'); return;
    }
    if (slug && !ambitoValido(grupoActual, ambito)) {
      setErr('Elige el ámbito de la regla: GRUPOLAE, Colaboradores o los dos.'); return;
    }
    if (triggerKind === 'fecha') {
      if (!slug) { setErr('En modo fecha tienes que elegir un tipo de artículo concreto.'); return; }
      if (!dateField) { setErr('Selecciona el campo de fecha del tipo elegido.'); return; }
      const n = Number(daysBefore);
      if (daysBefore === '' || !Number.isInteger(n) || n < 0) {
        setErr('Los días de antelación deben ser un entero ≥ 0.'); return;
      }
    }
    if (!chips.length && !personasSel.length && !colabSel.length && !notifyAssignee) {
      setErr('Indica al menos un destinatario (email, usuario, colaborador o "usuario asignado").');
      return;
    }
    if (notifyAssignee && slug && userLinkOptions.length > 1 && !assigneeField) {
      setErr('Elige qué campo de usuario usar.');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        trigger_kind: triggerKind,
        trigger_event: triggerKind === 'evento' ? triggerEvent : null,
        asset_type_slug: slug || null,
        // Ámbito (Fase 4): null = los dos. Sin tipo («Todos los tipos»), siempre los dos.
        scope: scopeDelPayload(grupoActual, ambito),
        date_field: triggerKind === 'fecha' ? dateField : null,
        days_before: triggerKind === 'fecha' ? Number(daysBefore) : null,
        recipients: chips.join(', '),
        recipient_person_ids: personasSel.map((p) => p.id).join(','),
        recipient_colab_ids: colabSel.map((p) => p.id).join(','),
        notify_assignee: notifyAssignee,
        notify_inapp: notifyInapp,
        assignee_field: notifyAssignee
          ? (assigneeField ||
             (slug && userLinkOptions.length === 1 ? userLinkOptions[0].id : null))
          : null,
        subject: subject || null,
        body: body || null,
        is_active: isActive,
      };
      await onSubmit(payload);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  }

  async function handleSendTest() {
    setTestMsg(null);
    setErr(null);
    const dest = (testEmail || '').trim();
    if (!dest) {
      setErr('Escribe el email al que enviar la prueba.');
      return;
    }
    if (!isEmail(dest)) {
      setErr(`"${dest}" no es un email válido`);
      return;
    }
    // Contexto real: el tipo de la regla (si lo hay) y, para stock_bajo, unos
    // valores de ejemplo de empresa/cantidad/minimo. El backend mezcla esto
    // sobre DUMMY_VARS, así que las variables que NO mandemos caen en muestras.
    const nombreTipo = tipoActual?.nombre || 'Tipo de ejemplo';
    const vars = { tipo: nombreTipo };
    if (triggerKind === 'evento' && triggerEvent === 'stock_bajo') {
      Object.assign(vars, {
        evento: 'Stock bajo',
        empresa: 'Empresa de ejemplo',
        cantidad: '3',
        minimo: '5',
        usuario: '',
      });
    }
    try {
      setBusy(true);
      await api.post('/alertas/reglas/test', {
        subject,
        body, // HTML del editor (react-quill)
        to: dest,
        vars,
      });
      setTestMsg(`Email de prueba enviado a ${dest}.`);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'No se pudo enviar la prueba');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 text-sm">
      {/* Nombre */}
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Nombre</label>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={100}
          className="w-full border border-slate-200 rounded-md px-3 py-2"
          placeholder="p. ej. Aviso ITV de coches"
        />
      </div>

      {/* Modo de disparo */}
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Disparador</label>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setTriggerKind('evento')}
            className={`px-3 py-1.5 text-sm transition-colors ${
              triggerKind === 'evento'
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Por evento
          </button>
          <button
            type="button"
            onClick={() => setTriggerKind('fecha')}
            className={`px-3 py-1.5 text-sm transition-colors border-l border-slate-200 ${
              triggerKind === 'fecha'
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Por vencimiento (fecha)
          </button>
        </div>
      </div>

      {/* Bloque condicional: evento o fecha */}
      {triggerKind === 'evento' ? (
        <>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">
              Evento que dispara la regla
            </label>
            <SearchableSelect
              value={triggerEvent}
              onChange={setTriggerEvent}
              options={TRIGGER_EVENTS}
              placeholder="Selecciona un evento"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">
              Tipo de artículo (opcional)
            </label>
            <SearchableSelect
              value={slugSel}
              onChange={elegirTipo}
              options={tipoOptions}
              disabled={tiposLoading}
              placeholder={tiposLoading ? 'Cargando tipos…' : 'Todos los tipos'}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Vacío = la regla aplica a cualquier tipo cuando ocurre el evento.
            </p>
          </div>
          {selectorAmbito}
        </>
      ) : (
        <>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">
              Tipo de artículo
            </label>
            <SearchableSelect
              value={slugSel}
              onChange={(v) => { elegirTipo(v); setDateField(''); }}
              options={tipoOptionsFecha}
              disabled={tiposLoading}
              placeholder={tiposLoading ? 'Cargando tipos…' : 'Elige un tipo'}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              En modo fecha el tipo es obligatorio (los campos fecha viven en su schema).
            </p>
          </div>
          {selectorAmbito}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Campo fecha</label>
              <SearchableSelect
                value={dateField}
                onChange={setDateField}
                options={dateFieldOptions}
                disabled={!slug || dateFieldOptions.length === 0}
                placeholder={
                  !slug
                    ? 'Elige primero un tipo'
                    : dateFieldOptions.length === 0
                      ? 'El tipo no tiene campos fecha'
                      : 'Selecciona un campo fecha'
                }
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
                Días de antelación
                <HelpTip>Con cuántos días de adelanto avisar antes de la fecha.</HelpTip>
              </label>
              <input
                type="number"
                min="0"
                value={daysBefore}
                onChange={(e) => setDaysBefore(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-xs"
                placeholder="15"
              />
            </div>
          </div>
        </>
      )}

      {/* Destinatarios (chips) */}
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Destinatarios</label>
        <div className="w-full border border-slate-200 rounded-md px-2 py-1.5 flex flex-wrap gap-1.5">
          {chips.map((c, idx) => (
            <span
              key={`${c}-${idx}`}
              className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full"
            >
              {c}
              <button
                type="button"
                onClick={() => removeChip(idx)}
                className="text-slate-400 hover:text-rose-600"
                aria-label={`Quitar ${c}`}
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            value={chipInput}
            onChange={(e) => { setChipInput(e.target.value); setChipError(null); }}
            onKeyDown={handleChipKey}
            onBlur={handleChipBlur}
            className="flex-1 min-w-[140px] outline-none text-xs font-mono py-0.5"
            placeholder={chips.length === 0 ? 'rrhh@grupolae.net, jefe@grupolae.net' : 'añadir email…'}
          />
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          Enter o coma para añadir. Backspace borra el último.
        </p>
        {chipError && (
          <p className="text-[11px] text-rose-600 mt-1">{chipError}</p>
        )}

        {personas.length > 0 && (
          <div className="mt-2">
            <SearchableSelect
              value=""
              onChange={(val) => {
                const id = Number(val);
                if (!id) return;
                const p = personas.find((x) => x.Id === id);
                if (!p) return;
                if (personasSel.some((s) => s.id === id)) return;
                const nombre = [p.nombre, p.apellidos].filter(Boolean).join(' ') || `#${id}`;
                setPersonasSel((prev) => [...prev, { id, nombre }]);
              }}
              options={personaOptions}
              placeholder="Añadir usuario del sistema…"
            />
          </div>
        )}
        {personasSel.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {personasSel.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
              >
                {p.nombre}
                <button
                  type="button"
                  onClick={() => setPersonasSel((prev) => prev.filter((x) => x.id !== p.id))}
                  className="text-blue-400 hover:text-rose-600"
                  aria-label={`Quitar ${p.nombre}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {colaboradores.length > 0 && (
          <div className="mt-2">
            <SearchableSelect
              value=""
              onChange={(val) => {
                const id = Number(val);
                if (!id) return;
                const p = colaboradores.find((x) => x.Id === id);
                if (!p) return;
                if (colabSel.some((s) => s.id === id)) return;
                const nombre = [p.nombre, p.apellidos].filter(Boolean).join(' ') || `#${id}`;
                setColabSel((prev) => [...prev, { id, nombre }]);
              }}
              options={colabOptions}
              placeholder="Añadir colaborador…"
            />
          </div>
        )}
        {colabSel.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {colabSel.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full"
              >
                {p.nombre}
                <button
                  type="button"
                  onClick={() => setColabSel((prev) => prev.filter((x) => x.id !== p.id))}
                  className="text-violet-400 hover:text-rose-600"
                  aria-label={`Quitar ${p.nombre}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={notifyAssignee}
            onChange={(e) => setNotifyAssignee(e.target.checked)}
          />
          <span className="text-sm text-slate-700">
            Incluir también al usuario asignado del activo
          </span>
        </label>
        {notifyAssignee && slug && userLinkOptions.length > 1 && (
          <div className="mt-2">
            <label className="text-xs text-slate-500 mb-1 block">
              ¿Qué campo de usuario?
            </label>
            <SearchableSelect
              value={assigneeField}
              onChange={setAssigneeField}
              options={userLinkOptions}
              placeholder="Elige el campo de usuario"
            />
          </div>
        )}
        {notifyAssignee && slug && userLinkOptions.length === 1 && (
          <p className="text-[11px] text-slate-400 mt-1">
            Se usará el campo "{userLinkOptions[0].nombre}".
          </p>
        )}
        {notifyAssignee && !slug && (
          <p className="text-[11px] text-slate-400 mt-1">
            Se usará el usuario asignado de cada activo según su tipo.
          </p>
        )}

        <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={notifyInapp}
            onChange={(e) => setNotifyInapp(e.target.checked)}
          />
          <span className="text-sm text-slate-700">
            Avisar también en la app (campana) a los usuarios internos
          </span>
        </label>
      </div>

      {/* Asunto + body */}
      <div className="border-t border-slate-100 pt-3 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-slate-500">Asunto</label>
            <VariablesMenu
              label="Insertar en asunto"
              variables={variablesDisponibles}
              onInsert={insertarEnSubject}
            />
          </div>
          <input
            ref={subjectRef}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={255}
            className="w-full border border-slate-200 rounded-md px-3 py-2"
            placeholder="ITV de {{activo}} en {{dias}} días"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-slate-500">Cuerpo del mensaje</label>
            <VariablesMenu
              label="Insertar en cuerpo"
              variables={variablesDisponibles}
              onInsert={insertarEnBody}
            />
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Escribe el cuerpo del email. Usa el menú para insertar variables."
            />
          </div>
        </div>
      </div>

      {/* Estado + enviar prueba */}
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span className="text-sm text-slate-700">Regla activa</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => { setTestEmail(e.target.value); setTestMsg(null); }}
            placeholder="email para la prueba"
            className="border border-slate-200 rounded-md px-3 py-2 text-sm w-56"
          />
          <Button variant="secondary" onClick={handleSendTest} disabled={busy}>
            <Send className="w-4 h-4 mr-1" />
            Enviar prueba
          </Button>
        </div>
      </div>

      {err && (
        <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
          {err}
        </p>
      )}
      {testMsg && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
          {testMsg}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-3 mt-1 border-t border-slate-100">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={busy}>
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          {isEdit ? 'Guardar cambios' : 'Crear regla'}
        </Button>
      </div>
    </div>
  );
}


/**
 * Menú desplegable de variables. Mínimo, sin dependencias extra (no usamos
 * el Popover del DS para no añadir surface si no lo tenemos a mano).
 */
function VariablesMenu({ label, variables, onInsert }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-[hsl(var(--brand-light))] hover:underline"
      >
        + {label}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-md py-1">
          {variables.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => { onInsert(v.id); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between"
            >
              <span className="font-mono text-slate-700">{v.id}</span>
              <span className="text-[10px] text-slate-400">{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
